import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CompanyRegisterApiService, CompanyRegisterResult, TaxationModeInfo, VatPayerInfo } from 'src/app/services/company-register-api.service';

/**
 * UI-only wrapper around a confirmed lookup result plus which part of it
 * the manager asked to apply (AG-23d) — never sent over HTTP, so this does
 * not belong next to `CompanyRegisterResult` in `company-register-api.service.ts`.
 */
export interface CompanyRegisterApplyEvent {
  result: CompanyRegisterResult;
  /** Whatever this verify click's VAT lookup found, `null` if it wasn't
   *  fetched/supported/found — same object `onVerify()` stored in
   *  `vatInfo`, just handed onward so the host can map it too. Sourced
   *  either from `result.vatInfo` (opendatabot, embedded) or the separate
   *  `getVatInfo()` call (youcontrol) — see `onVerify()`. */
  vatInfo: VatPayerInfo | null;
  /** Same as `vatInfo` above but for taxation mode — always sourced from
   *  `result.taxationMode` (no provider has a separate call for this yet). */
  taxationMode: TaxationModeInfo | null;
  scope: 'all' | 'company' | 'person';
}

/**
 * Standalone "verify a registration code against an external company
 * register" widget (AG-23b) — general-purpose, not agent-specific. Takes
 * a registration code in, offers a manual "Verify" lookup, and emits the
 * result out; the host form decides what to do with it. Never writes
 * anything itself. A found result is reviewed in a modal (Update/Ignore)
 * before it's emitted, so applying it is always an explicit choice, not
 * something that just appears inline next to the field. See
 * docs/PROMPT_company-register-verify.md for the full design.
 *
 * AG-23d: the modal's single "Оновити" is now a split button — company
 * fields and the director name can drift independently in practice (a
 * client's company fields are often already correct while only the
 * director is stale), so the manager can apply "all," "company only," or
 * "person only." See docs/PROMPT_company-register-apply-refinements.md.
 *
 * Provider picker (added 2026-09-01): the owner subscribed to more than one
 * registry service and wants to choose which one to query per click,
 * instead of a single fixed backend default. The main "Перевірити в
 * реєстрі" button is now a split button too when more than one provider is
 * available — main click keeps today's behavior (no explicit provider,
 * backend's `COMPANY_REGISTER_PROVIDER` default decides), the dropdown
 * lists each available service by name for a one-off explicit choice. With
 * zero or one provider configured, the plain button renders instead (see
 * the template) — a dropdown arrow with nothing to choose between is worse
 * than no dropdown.
 */
@Component({
  selector: 'app-company-register-verify',
  templateUrl: './company-register-verify.component.html',
  styleUrls: ['./company-register-verify.component.scss'],
})
export class CompanyRegisterVerifyComponent implements OnInit {
  /** Registration code to verify. The component does not read this from
   *  anywhere else — the host form owns the field and passes the current
   *  value in. */
  @Input() registrationCode: string | null = null;

  /** Emitted when the manager clicks "Apply" (or a split-button option) on
   *  a found result — the host form decides what to do with it. This
   *  component never writes into anything itself. */
  @Output() apply = new EventEmitter<CompanyRegisterApplyEvent>();

  applyMenuItems: MenuItem[] = [
    { label: 'Тільки дані компанії', icon: 'pi pi-building', command: () => this.onApply('company') },
    { label: 'Тільки керівника', icon: 'pi pi-user', command: () => this.onApply('person') },
  ];

  /** Populated from `GET /api/company-register/providers` in `ngOnInit()`.
   *  Empty (not just absent) means either the fetch hasn't returned yet,
   *  failed, or only one/zero providers are configured — the template's
   *  `*ngIf="verifyMenuItems.length > 1"` covers all three the same way:
   *  fall back to the plain button, main-click behavior is unaffected
   *  either way. */
  verifyMenuItems: MenuItem[] = [];

  result: CompanyRegisterResult | null = null;
  /** Fetched alongside `result` on every verify click (owner decision,
   *  2026-08-31) — best-effort: not every configured backend provider
   *  implements VAT lookup, so a failure here (including a plain 501)
   *  never blocks or errors the main register-verify result. `null` means
   *  either not yet fetched, unsupported, or the code isn't a VAT payer
   *  (this endpoint's 404 doesn't distinguish those — see
   *  `youcontrol-provider.ts`'s `getVatInfo()`), so the template only ever
   *  renders it when `vatInfo?.found`. */
  vatInfo: VatPayerInfo | null = null;
  /** Fetched alongside `result`/`vatInfo`, always sourced from
   *  `result.taxationMode` — see `onVerify()`. `null` means either not yet
   *  fetched, unsupported by the configured provider, or nothing found. */
  taxationMode: TaxationModeInfo | null = null;
  loading: boolean = false;
  /** No language string here on purpose — the actual message lives in the
   *  template, so a future i18n pass has one place to find it instead of
   *  a string built in component logic. */
  error: boolean = false;

  /** A found result is reviewed in a modal (Update/Ignore) rather than
   *  inline, so the manager makes an explicit choice instead of the data
   *  just appearing in the row next to the field. Not-found/error stay
   *  inline — there's no choice to make on those, just a status. */
  showResultDialog: boolean = false;

  constructor(private companyRegisterApi: CompanyRegisterApiService) { }

  ngOnInit(): void {
    this.companyRegisterApi.getAvailableProviders().subscribe({
      next: ({ providers }) => {
        // Menu labels the explicit choice; the plain click (no provider
        // argument) keeps using the backend's own default -- so this menu
        // is a set of one-off alternatives, not a "current selection"
        // picker, same relationship applyMenuItems already has to onApply().
        this.verifyMenuItems = providers.map((p) => ({
          label: `Перевірити через ${p.name}`,
          command: () => this.onVerify(p.name),
        }));
      },
      // Best-effort, same fail-open principle as everything else in this
      // component -- the plain "Перевірити в реєстрі" button (backend
      // default provider) still works with no menu at all.
      error: () => { this.verifyMenuItems = []; },
    });
  }

  onVerify(provider?: string): void {
    if (!this.registrationCode?.trim()) { return; }
    const code = this.registrationCode.trim();
    this.loading = true;
    this.error = false;
    this.result = null;
    this.vatInfo = null;
    this.taxationMode = null;

    this.companyRegisterApi.lookup(code, provider).pipe(
      // A provider that already embeds VAT data on the lookup result
      // (opendatabot, `result.vatInfo !== undefined`) needs no second call
      // -- see CompanyRegisterResult.vatInfo's doc comment for why that
      // matters (avoids paying for the same vendor call twice). Only a
      // provider that genuinely doesn't embed it (youcontrol, `undefined`)
      // falls back to the separate best-effort `/vat` call, same fail-open
      // treatment as before: never blocks or errors the main result. The
      // SAME explicit `provider` (if any) is passed through here so the
      // fallback queries the service the manager actually picked, not
      // whatever the backend's own default happens to be.
      switchMap((result) => {
        if (result.vatInfo !== undefined) {
          return of({ result, vatInfo: result.vatInfo });
        }
        return this.companyRegisterApi.getVatInfo(code, provider).pipe(
          map((vatInfo) => ({ result, vatInfo })),
          catchError(() => of({ result, vatInfo: null as VatPayerInfo | null })),
        );
      }),
    ).subscribe({
      next: ({ result, vatInfo }) => {
        this.result = result;
        this.vatInfo = vatInfo;
        this.taxationMode = result.taxationMode ?? null;
        this.loading = false;
        this.showResultDialog = result.found;
      },
      error: () => {
        // Fail-open per AG-23a's contract: a lookup failure means "couldn't
        // verify," never "company doesn't exist." Never block the host
        // form's own save action on this component's state.
        this.error = true;
        this.loading = false;
      },
    });
  }

  onApply(scope: 'all' | 'company' | 'person' = 'all'): void {
    if (this.result?.found) {
      this.apply.emit({ result: this.result, vatInfo: this.vatInfo, taxationMode: this.taxationMode, scope });
    }
    this.showResultDialog = false;
  }

  onIgnore(): void {
    this.showResultDialog = false;
  }
}
